import React from 'react';
import { observer } from 'mobx-react';

import AddCommentActivities from 'ui/AddCommentActivities';
import FeedComponent from 'ui/Feed';
import { DefaultFeedMapping } from 'ui/Feed/Feed';

import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';

import API from 'utils/api';
import { ActivityMapping } from 'utils/api/types';
import { USER_TAG_BASE } from 'core/constants';

import SessionUser from 'stores/Session/SessionUser';
import AuditQuestionActivities from 'stores/AuditQuestionActivities';

import { useStore } from 'utils/hooks/store';

import TaskLinkActivity from './TaskLinkActivity';
import { Composer, EmptyState, RetryRow, ScrollableContainer, TabBody } from '../styles';

// Feed silently drops activities whose type has no mapping, so task-links must be mapped here.
const activityMapping = {
  ...DefaultFeedMapping,
  task_link: TaskLinkActivity,
} as unknown as ActivityMapping;

const ActivityTab = ({
  activities,
  questionKey,
}: {
  activities: AuditQuestionActivities;
  questionKey: string;
}): React.ReactElement => {
  const { session } = useStore();

  // `error` is the terminal condition: a rejected load leaves `activities` undefined and `isLoading`
  // false, which would otherwise refire this effect for as long as the drawer is open.
  React.useEffect(() => {
    activities.ensureFeed(questionKey);
  }, [activities, questionKey, activities.activities, activities.isLoading, activities.error]);

  if (activities.error) {
    return (
      <TabBody>
        <EmptyState>Activity could not be loaded.</EmptyState>
        <RetryRow>
          <Button variant="outlined" size="small" onClick={activities.retryFeed}>
            Try again
          </Button>
        </RetryRow>
      </TabBody>
    );
  }

  if (activities.activities === undefined) {
    return (
      <TabBody>
        <CenteredCircularProgress />
      </TabBody>
    );
  }

  return (
    <TabBody>
      <ScrollableContainer>
        {activities.activities.length === 0 ? (
          <EmptyState>No activity on this question yet</EmptyState>
        ) : (
          <FeedComponent
            // @ts-ignore — Feed is typed against the base store's generic pair
            originalActivities={activities}
            mapping={activityMapping}
            onDeleteComment={activities.deleteMessage}
            onDeleteFile={activities.deleteFile}
            sessionUser={session.user as SessionUser}
            downloadFile={API.downloadFile}
            userTagBaseUrl={USER_TAG_BASE}
            disableAutoScroll
          />
        )}
      </ScrollableContainer>
      <Composer data-testid="audit-question-comment-input">
        <AddCommentActivities
          placeholder="Post an update"
          uploadFile={activities.uploadFile}
          sendMessage={activities.addMessage}
          sendTestId="audit-question-comment-submit"
        />
      </Composer>
    </TabBody>
  );
};

export default observer(ActivityTab);
